import {Notifications} from "./notifications";
import {authApi, apiVersion} from "./api";
import {FreezeUI} from "./freeze-ui";
import {requestVerificationToken} from "./common";
import {memberSearchTagify} from "./tagify";
import {Guid} from "./guid";
import {trackException} from "./errors";

let editor, subjectEl, tagify, attachmentDropzone, id, modal;
export const maxFileSize = 5; // Maximum size in MB
export const maxAttachments = 5; // Maximum number of attachments allowed per email

export const parallelUploads = 4;

const Emails = function () {

    return {
        init: async () => {
            document.addEventListener("click", async e => {
                const target = e.target.closest("[data-send-email]")
                if (!target)
                    return;

                e.preventDefault();
                const memberId = target.dataset.memberId;
                const name = target.dataset.memberName;

                await composeEmail([{value: memberId, text: name}]);
            });
        }
    }
}();

export const initDropzone = (selector, id) => {

    const container = document.querySelector(selector);

    const previewNode = container.querySelector(".dropzone-item");
    previewNode.id = "";
    const previewTemplate = previewNode.parentNode.innerHTML;
    previewNode.parentNode.removeChild(previewNode);

    const dropzone = new Dropzone(selector, {
        url: `/api/emails/attachments/${id}`,
        headers: {
            RequestVerificationToken: requestVerificationToken,
            "x-api-version": apiVersion
        },
        previewTemplate: previewTemplate,
        parallelUploads: parallelUploads,
        maxFilesize: maxFileSize, // Max filesize in MB
        maxFiles: maxAttachments,
        autoQueue: true,
        previewsContainer: selector + " .dropzone-items",
        clickable: ".dropzone-select", // Define the element that should be used as click trigger to select files.
    });

    dropzone.on("addedfile", function (file) {
        const dropzoneItems = container.querySelectorAll('.dropzone-item');
        dropzoneItems.forEach(dropzoneItem => {
            dropzoneItem.style.display = '';
        });
    });

    dropzone.on("totaluploadprogress", function (progress) {
        const progressBars = container.querySelectorAll('.progress-bar');
        progressBars.forEach(progressBar => {
            progressBar.style.width = progress + "%";
        });
    });

    dropzone.on("sending", function (file) {
        // Show the total progress bar when upload starts
        const progressBars = container.querySelectorAll('.progress-bar');
        progressBars.forEach(progressBar => {
            progressBar.style.opacity = "1";
        });
    });

    dropzone.on("complete", function (progress) {
        const progressBars = container.querySelectorAll('.dz-complete');

        setTimeout(function () {
            progressBars.forEach(progressBar => {
                progressBar.querySelector('.progress-bar').style.opacity = "0";
                progressBar.querySelector('.progress').style.opacity = "0";
            });
        }, 300);
    });

    dropzone.on("removedfile", async file => {
        await removeAttachment(id, file);
    });

    if(container.dataset.attachments) {
        const attachments = JSON.parse(container.dataset.attachments);

        attachments?.forEach(attachment => {
            const mockFile = {
                name: attachment.name,
                size: attachment.size,
                type: attachment.contentType,
                accepted: true
            };
            dropzone.files.push(mockFile);
            dropzone.emit("addedfile", mockFile);
            dropzone.emit("complete", mockFile);
        });
    }

    return dropzone;
}

export const composeEmail = async (to, subject, body) => {
    id = Guid.newGuid();
    const placeholderEl = document.querySelector("#modal-placeholder");
    const {data} = await axios.get("/compose-email");

    const modalHtml = new DOMParser().parseFromString(data, 'text/html');
    placeholderEl.append(...modalHtml.body.children);
    const modalEl = placeholderEl.querySelector(".modal");
    modal = new bootstrap.Modal(modalEl);

    const recipientsEl = placeholderEl.querySelector("#To");
    subjectEl = placeholderEl.querySelector("#Subject");

    subjectEl.value = subject ?? "";

    initTagify(recipientsEl, to);
    initEditor(modalEl, "#editor")
    attachmentDropzone = initDropzone("#attachment_dropzone", id);

    placeholderEl.querySelector("#send-email").addEventListener("click", (e) => {
        e.preventDefault();

        Notifications.confirm.fire({ text: "Are you sure you want to send the email?" })
            .then(async (result) => {
                if (result.isConfirmed) {
                    await saveEmail(true);
                }
            });
    });

    placeholderEl.querySelector("#advanced_editor")?.addEventListener("click", async (e) => {
        e.preventDefault();

        await saveEmail(false);
    });

    modal.show();

    modalEl.addEventListener('hide.bs.modal', e => {
        placeholderEl.innerHTML = "";
        tagify = null;
        editor = null;
    });
}

//#region Editor
const initEditor = (container, selector) => {

    // Init editor
    const editorEl = document.querySelector(selector)
    editor = CKEditor.ClassicEditor
        .create(editorEl, {
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'indent',
                        'outdent',
                        'alignment',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        'blockQuote',
                        'horizontalLine',
                        'insertTable',
                    ]
                }
            })
        .then(e => {
            editor = e;
        })
        .catch(error => {
            console.error(error);
        });
};

//#endregion

//#region Tagify

const initTagify = (el, to) => {

    tagify = memberSearchTagify("#To", true);
    tagify.whitelist = to;
    tagify.addTags(to);
};

//#endregion

const saveEmail = async (sendEmail) => {
    const body = editor.getData();
    const subject = subjectEl.value;
    const recipients = tagify.value.map(x => ({value: x.value, text: x.text}));

    if(sendEmail && recipients.length <= 0){
        Notifications.showWarning({ message: "Please select at least one participant before sending." });
        return;
    }

    if (sendEmail && subject.length <= 0) {
        Notifications.showWarning({ message: "Please enter a subject." });
        return;
    }

    if(sendEmail && body.length <= 1){
        Notifications.showWarning({ message: "Please enter a message body." });
        return;
    }

    const values = {
        id,
        subject,
        body,
        recipients,
        status: sendEmail ? "Queued" : "Draft"
    };

    FreezeUI.Freeze();

    try{
        const {data} = await authApi.put(`/api/emails/${id}`, values);

        if(sendEmail){
            FreezeUI.UnFreeze();
        } else {
            window.location.href = `/admin/emails/compose/${id}`;
        }
        Notifications.showSuccess({ message: "Your email has been sent." });
        modal.hide();
    }
    catch (err) {
        trackException(err);
        Notifications.showError({ message: "There was a problem saving the email. Please try again later." });
    }
    finally {
        FreezeUI.UnFreeze();
    }
}

const removeAttachment = async (id, file) => {

    const {data} = await authApi.delete(`/api/emails/attachments/${id}`, {
        data: {
            fileName: file.name
        }
    });

    console.log(data);
}

export default Emails;
